import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check, Clock, ArrowLeft, Sparkles, BookOpen, Users, ClipboardList, Heart } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CallButton, WhatsAppButton } from "@/components/CTAs";
import BookingForm from "@/components/BookingForm";
import ServiceCard from "@/components/ServiceCard";
import { SERVICES } from "@/data/site";

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="flex items-center gap-2.5 font-serif-display text-2xl text-cream sm:text-3xl">
    <Icon size={20} className="text-gold" /> {children}
  </h2>
);

const CheckList = ({ items }) => (
  <ul className="mt-6 grid gap-4 sm:grid-cols-2">
    {items.map((e) => (
      <li key={e} className="flex items-start gap-3 text-sm text-stone-300">
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gold/15">
          <Check size={12} className="text-gold" />
        </span>
        {e}
      </li>
    ))}
  </ul>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  if (!service) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-5 text-center">
        <h1 className="font-serif-display text-4xl text-cream">Service not found</h1>
        <Link to="/services-luxury-spa" data-testid="service-not-found-back" className="text-gold underline underline-offset-4">
          Back to all services
        </Link>
      </main>
    );
  }

  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <main data-testid={`service-page-${service.slug}`} className="pb-16 md:pb-0">
      <Seo
        title={`${service.name} in Suchitra, Hyderabad | Belong Saloon & Spa`}
        description={`${service.name} at Belong Saloon & Spa, Suchitra Hyderabad. ${service.tagline}. Sessions: ${service.durations.map((d) => d.mins).join("/")} min. Call 098666 34473.`}
      />

      <section ref={ref} className="relative flex min-h-[72vh] items-end overflow-hidden">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={service.image} alt={service.alt} fetchPriority="high" className="h-[120%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/45 to-obsidian" />
        </motion.div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-44 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Link
              to="/services-luxury-spa"
              data-testid="service-back-link"
              className="mb-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-stone-300 transition-colors hover:text-gold"
            >
              <ArrowLeft size={14} /> All services
            </Link>
            <h1 className="max-w-3xl font-serif-display text-4xl leading-tight text-cream sm:text-6xl">
              {service.name}
            </h1>
            <p className="mt-4 max-w-xl text-base italic leading-relaxed text-stone-300 sm:text-lg">
              {service.tagline}
            </p>
            {service.special && (
              <p data-testid="service-special-badge" className="mt-5 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-obsidian/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold backdrop-blur">
                <Sparkles size={13} /> Signature service · starts at 60 min
              </p>
            )}
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        <div className="grid gap-14 lg:grid-cols-[1.3fr_1fr]">
          <div className="space-y-14">
            <Reveal>
              <SectionTitle icon={BookOpen}>What It Is</SectionTitle>
              <p className="mt-5 text-base leading-relaxed text-stone-300 sm:text-lg">{service.description}</p>
              <p className="mt-4 text-base leading-relaxed text-stone-400">{service.detail}</p>
            </Reveal>

            <Reveal>
              <SectionTitle icon={Users}>Who It Is For</SectionTitle>
              <CheckList items={service.whoFor} />
            </Reveal>

            <Reveal>
              <SectionTitle icon={ClipboardList}>What to Expect</SectionTitle>
              <CheckList items={service.expect} />
            </Reveal>

            <Reveal>
              <SectionTitle icon={Heart}>Benefits</SectionTitle>
              <div className="mt-6 flex flex-wrap gap-3">
                {service.benefits.map((b) => (
                  <span key={b} className="rounded-full border border-gold/20 bg-ember px-4 py-2 text-sm text-stone-300">
                    {b}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div data-testid="duration-price-table" className="gold-ring sticky top-28 rounded-2xl bg-coal p-8">
              <h2 className="flex items-center gap-2 font-serif-display text-2xl text-cream">
                <Clock size={20} className="text-gold" /> Session Durations
              </h2>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {service.durations.map((d) => (
                  <div key={d.mins} data-testid={`price-row-${d.mins}`} className="rounded-xl border border-gold/20 bg-ember px-4 py-4 text-center">
                    <span className="font-serif-display text-3xl text-gold">{d.mins}</span>
                    <span className="block text-xs uppercase tracking-[0.2em] text-stone-400">minutes</span>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-stone-500">
                Call or WhatsApp us for current pricing, couple bookings and today's offers.
              </p>
              <div className="mt-7 flex flex-col gap-3">
                <CallButton testid="service-call-button" label="Call to Book" className="w-full" />
                <WhatsAppButton testid="service-whatsapp-button" service={service.name} className="w-full" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="service-booking-section" className="border-t border-gold/10 bg-ember/50 py-20 sm:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr]">
          <Reveal>
            <Eyebrow className="mb-4">Reserve This Service</Eyebrow>
            <h2 className="font-serif-display text-3xl leading-tight text-cream sm:text-5xl">
              Book your <span className="italic text-gold-gradient">{service.short}</span> session
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-stone-400">
              Choose a duration and a preferred date — your request opens in WhatsApp, ready to send.
              We confirm within minutes.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="gold-ring rounded-3xl bg-coal p-7 sm:p-9">
              <BookingForm key={service.slug} presetService={service.slug} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-12">
            <Eyebrow className="mb-4">Continue Exploring</Eyebrow>
            <h2 className="font-serif-display text-3xl text-cream sm:text-4xl">More services</h2>
          </Reveal>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((s, i) => (
              <ServiceCard key={s.slug} service={s} index={i} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
